import { observer } from 'mobx-react-lite'
import { Fragment, type ReactElement } from 'react'
import { groupBy } from 'lodash'
import { Divider, type IIconProps, Scrollbar } from 'shared/ui'
import { FlowDropdownButton, FlowDropdownLabel } from 'widgets/Flow'
import { useSmartCallFlowBuilderContext } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/context'
import {
  CallQueueStore,
  ForwardCallStore,
  PlayAudioStore,
  RingStore,
  SendToVoicemailStore,
} from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions'
import { type ISmartCallFlowActionType } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/type'

type IAddActionDropdownProps = {
  setOpen: (value: boolean) => void
  source: string
  target: string
}

type IAction = {
  type: Exclude<ISmartCallFlowActionType, 'incomingCall' | 'callEnds'>
  group: string
  element: ReactElement
}

type IMakeActionProps = {
  type: IAction['type']
  group: IAction['group']
  iconProps: IIconProps
  title: string
  onClick: () => void
}

export const AddActionDropdownContent = observer(
  ({ source, target, setOpen }: IAddActionDropdownProps) => {
    const { addActionByType, getActionById } = useSmartCallFlowBuilderContext()

    const sourceAction = getActionById(source)
    const targetAction = getActionById(target)

    const handleClick = (handler: () => void) => () => {
      handler()
      setOpen(false)
    }

    const makeAction = ({ type, group, iconProps, title, onClick }: IMakeActionProps): IAction => ({
      type: type,
      group: group,
      element: <FlowDropdownButton iconProps={iconProps} text={title} onClick={onClick} />,
    })

    const allActionsMap: Record<IAction['type'], IMakeActionProps> = {
      ring: {
        type: 'ring',
        group: 'Actions',
        title: RingStore.title,
        iconProps: RingStore.iconProps,
        onClick: handleClick(() => addActionByType('ring', source, target)),
      },
      call_queue: {
        type: 'call_queue',
        group: 'Actions',
        title: CallQueueStore.title,
        iconProps: CallQueueStore.iconProps,
        onClick: handleClick(() => addActionByType('call_queue', source, target)),
      },
      sendToVoicemail: {
        type: 'sendToVoicemail',
        group: 'Actions',
        title: SendToVoicemailStore.title,
        iconProps: SendToVoicemailStore.iconProps,
        onClick: handleClick(() => addActionByType('sendToVoicemail', source, target)),
      },
      playAudio: {
        type: 'playAudio',
        group: 'Actions',
        title: PlayAudioStore.title,
        iconProps: PlayAudioStore.iconProps,
        onClick: handleClick(() => addActionByType('playAudio', source, target)),
      },
      ivr: {
        type: 'ivr',
        group: 'Actions',
        title: 'IVR phone tree',
        iconProps: {
          icon: 'dataflow',
          color: 'var(--action-contained-primary-normal)',
        },
        onClick: handleClick(() => console.log('IVR phone tree')),
      },
      forwardCall: {
        type: 'forwardCall',
        group: 'Actions',
        title: ForwardCallStore.title,
        iconProps: ForwardCallStore.iconProps,
        onClick: handleClick(() => addActionByType('forwardCall', source, target)),
      },
      sendSMS: {
        type: 'sendSMS',
        group: 'Actions',
        title: 'Send SMS',
        iconProps: {
          icon: 'messageTextCircle02',
          color: 'var(--action-contained-primary-normal)',
        },
        onClick: handleClick(() => console.log('Send SMS')),
      },
      businessHours: {
        type: 'businessHours',
        group: 'Logic',
        title: 'Business hours',
        iconProps: {
          icon: 'clock',
          color: 'var(--custom-green-custom)',
        },
        onClick: handleClick(() => console.log('Business hours')),
      },
      aiTextingAgent: {
        type: 'aiTextingAgent',
        group: 'AI',
        title: 'AI Texting Agent',
        iconProps: {
          icon: 'stars',
          color: 'var(--purple-70)',
        },
        onClick: handleClick(() => console.log('AI Texting Agent')),
      },
      aiCallingAgent: {
        type: 'aiCallingAgent',
        group: 'AI',
        title: 'AI Calling Agent',
        iconProps: {
          icon: 'stars',
          color: 'var(--purple-70)',
        },
        onClick: handleClick(() => console.log('AI Calling Agent')),
      },
    }

    const actions = Object.values(allActionsMap)
      .filter((item) => {
        const type = item.type
        let isValid = true
        if (
          sourceAction?.actionStore.allowedNextActions &&
          !sourceAction?.actionStore.allowedNextActions.includes(type)
        ) {
          isValid = false
        }
        if (
          targetAction?.actionStore.allowedBeforeActions &&
          !targetAction?.actionStore.allowedBeforeActions.includes(type)
        ) {
          isValid = false
        }
        return isValid
      })
      .map(makeAction)

    return (
      <Scrollbar autoHeight autoHeightMax={440}>
        {Object.entries(groupBy(actions, 'group')).map(([group, values], index) => (
          <Fragment key={group}>
            {index !== 0 && <Divider sx={{ marginBlock: '5.5px' }} />}
            <FlowDropdownLabel ariaLabel={group}>{group}</FlowDropdownLabel>
            {values.map((value) => (
              <Fragment key={value.type}>{value.element}</Fragment>
            ))}
          </Fragment>
        ))}
      </Scrollbar>
    )
  }
)
